import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { Preview } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Preview";
import { Prognosis } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Prognosis";
import { DropsLogTextInput } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/DropsLogTextInput";
import { SubTypeInputs } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs";
import { SelectIncidentsForTrain } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/IncidentsForTrain";
import {
  SingleTrainFormSchema,
  trainFormSchema,
  TrainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { singleTrainFormToRequestBody } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/transformers";
import { GroupedInformationResponse } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useInfrastructureInformationForTrain";
import { formStateHasCustomField } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/utils";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { supportsMultipleEvents } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/utils";
import { FormFieldWithCustomInput } from "features/CenterContent/shared/operationalInformation/components/FormFieldWithCustomInput";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { filterOpInfoTypesTrain } from "features/CenterContent/shared/operationalInformation/utils";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { DropsBadge, Alert, Stack, Text } from "shared/components/Spor";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { useSinglePreview } from "shared/hooks/useSinglePreview";
import { countryCode as countryCodeEnum } from "shared/types/countryCode";
import { getTrainCustomInputs } from "shared/types/form";
import { useDropsRole } from "stores/useDropsRole";

type TrainInfoFormProps = {
  selectedTrain: TrainIdentifier;
  trainInfoWithOpenState: string[];
  opInfoTypes: OperationalInformationTypes;
  infrastructureData: GroupedInformationResponse | undefined;
  currentFormValues: TrainFormSchema;
  editingEventType?: TrainEventTypeEnum;
};

export type CommonTrainInfoFormProps = {
  mode: "edit" | "create";
  uuid?: string;
};

export const BadgeText = ({
  selectedTrain,
}: {
  selectedTrain: TrainIdentifier;
}) => (
  <Text variant="sm">
    Tog:
    <DropsBadge
      borderRadius="9px"
      paddingY="3px"
      marginX="3px"
      semantics="success"
    >
      {selectedTrain.identifier}
    </DropsBadge>
  </Text>
);

// `dropsLogText` is autofilled with text from the preview endpoint, but it is
// a required field when enabled. This can prevent an otherwise correct form
// from fetching an updated `dropsLogText` because the `dropsLogText` is missing
// until it's been fetched. The field has no effect on produced texts, so we
// simply disable it for the preview form, preventing it from blocking itself.
function getPreviewFormValues(formValues: TrainFormSchema): TrainFormSchema {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- single special case
  switch (formValues.type) {
    case TrainEventTypeEnum.TRAIN_CUSTOM:
      return formValues;

    default: {
      const newFormValues: TrainFormSchema = {
        ...formValues,
        dropsLogText: { ...formValues.dropsLogText, enabled: false },
      };

      return newFormValues;
    }
  }
}

const TrainInfoForm: FC<CommonTrainInfoFormProps & TrainInfoFormProps> = ({
  mode,
  uuid,
  selectedTrain,
  trainInfoWithOpenState,
  opInfoTypes,
  infrastructureData,
  currentFormValues,
  editingEventType,
}) => {
  const { t } = useTranslation();
  const { isDriftledningen } = useDropsRole();
  const { events, reasons, actions } = filterOpInfoTypesTrain(opInfoTypes);

  const { previewData, previewStatus } = useSinglePreview(
    trainFormSchema,
    getPreviewFormValues(currentFormValues),
    "trainInformation/singleTrainPreview",
    (formData) => singleTrainFormToRequestBody(formData, selectedTrain),
  );

  const trainCustomInputs = getTrainCustomInputs(t);

  const countryCode = useCountryCodeForDropsRole();

  return (
    <Stack gap="30px">
      <BadgeText selectedTrain={selectedTrain} />
      <Stack gap="12px">
        {infrastructureData && (
          <Stack marginBottom="18px" gap="12px">
            <SelectIncidentsForTrain items={infrastructureData} />
          </Stack>
        )}
        <FormFieldWithCustomInput<SingleTrainFormSchema>
          items={events}
          {...trainCustomInputs.event}
          optionDisabledPredicate={(event) =>
            editingEventType !== (event.type as TrainEventTypeEnum) && // Ignores the event.type of the edited train
            !supportsMultipleEvents(event) &&
            trainInfoWithOpenState?.some((info) => info === event.type)
          }
        />
        <FormFieldWithCustomInput<SingleTrainFormSchema>
          items={reasons}
          {...trainCustomInputs.reason}
        />
        <FormFieldWithCustomInput<SingleTrainFormSchema>
          items={actions}
          {...trainCustomInputs.action}
        />
        {countryCode === countryCodeEnum.enum.SE && (
          <Prognosis formPrefix="trainForm" />
        )}
      </Stack>

      {currentFormValues?.type && (
        <Stack>
          <SubTypeInputs
            type={currentFormValues?.type}
            mode={mode}
            trainId={selectedTrain}
            uuid={uuid}
          />

          {currentFormValues.type !== TrainEventTypeEnum.TRAIN_CUSTOM &&
            !isDriftledningen() && (
              <DropsLogTextInput
                previewText={previewData?.internalMessage ?? ""}
              />
            )}
        </Stack>
      )}

      <Preview previewStatus={previewStatus} previewData={previewData} />

      {formStateHasCustomField(currentFormValues) && (
        <Alert data-color="info" id="infoBoxActionModal">
          {t(
            "Hendelsen inneholder egendefinert tekst. Sjekk at oppsummeringen ser riktig ut før du går videre.",
          )}
        </Alert>
      )}
    </Stack>
  );
};

export { TrainInfoForm };
